import { NewLeaversModalContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversModal';
import { NewLeaverInvestorLocal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/useNewLeaversModalController';
import { ContractType, DocumentWorkflowTemplateVisibleIn } from '@dealstack/shared/lib/generated';
import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

interface IContractAllSelection {
	contractID: string;
	isChecked: boolean;
}

export const useNewLeaversInvestorsTableController = (
	investorsList: NewLeaverInvestorLocal[],
	setInvestorsList: Dispatch<SetStateAction<NewLeaverInvestorLocal[]>>
) => {
	const { allContracts, securities, mipData, loanDefinitions } =
		useContext(NewLeaversModalContext);
	const [attachToAllInvestorCheckboxStateArray, setAttachToAllInvestorCheckboxStateArray] =
		useState<IContractAllSelection[]>([]);

	const onUpdateInvestorContracts = (contracts: string[], investorId: string) => {
		if (!investorId) {
			throw new Error('Missing investorId when assigning contract');
		}
		if (!investorsList) {
			return;
		}
		const investorsCopy = [...(investorsList ?? [])];
		const investorInstance = investorsCopy.find((investor) => investor.id === investorId);
		const investorIndex = investorsCopy.findIndex((investor) => investor.id === investorId);

		if (!investorInstance || investorIndex === -1) {
			throw new Error(
				`Error during updating contract ${contracts.join(', ')} for investor ${investorId}`
			);
		}
		investorsCopy[investorIndex] = { ...investorInstance, contracts };
		setInvestorsList(investorsCopy);
	};

	const mainContracts = useMemo(
		() =>
			allContracts
				?.filter((contract) =>
					contract.visibleIn?.includes(
						DocumentWorkflowTemplateVisibleIn.INVESTMENT_WORKFLOWS_SELLING
					)
				)
				.filter((contract) => contract.type !== ContractType.SIDE_LETTER),
		[allContracts]
	);

	const sideAgreements = useMemo(() => {
		return allContracts?.filter((contract) => contract.type === ContractType.SIDE_LETTER);
	}, [allContracts]);

	const totalSideAgreementsAssigned = investorsList?.reduce((total, investor) => {
		const investorSideAgreements = investor?.contracts
			?.map((contractId) => allContracts?.find((item) => item?.id === contractId))
			?.filter((contract) => contract?.type === ContractType.SIDE_LETTER);
		if (investorSideAgreements?.length) {
			return (total += investorSideAgreements?.length);
		}
		return total;
	}, 0);

	//todo awrobel 04.10.2022 - merge logic of select all for all tables
	const updateContractsAllSelectionState = () => {
		const contracts = mainContracts ?? [];

		const updatedContractsAllSelection = contracts?.map((contract) => {
			if (!investorsList || investorsList?.length === 0) {
				return {
					contractID: contract.id,
					isChecked: false,
				};
			}
			return {
				contractID: contract.id,
				isChecked: investorsList.every((investor) => {
					return !!investor?.contracts?.find(
						(checkedContract) => checkedContract === contract.id
					);
				}),
			};
		});
		if (updatedContractsAllSelection?.length === 0) {
			return;
		}

		setAttachToAllInvestorCheckboxStateArray(updatedContractsAllSelection);
	};

	useEffect(() => {
		if (!investorsList) {
			return;
		}
		updateContractsAllSelectionState();
	}, [investorsList]);

	return {
		onUpdateInvestorContracts,
		sideAgreements,
		mainContracts,
		totalSideAgreementsAssigned,
		securitiesList: securities,
		currency: mipData?.currency,
		attachToAllInvestorCheckboxStateArray,
		loanDefinitions,
	};
};
